
'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Mousewheel, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  ArrowDownIcon, ArrowUpIcon, SpeakerWaveIcon, SpeakerXMarkIcon,
} from '@heroicons/react/16/solid';
import {
  Avatar, LinkStyled, Modal, Spinner,
} from '@components/common';
import { useTranslation } from 'next-i18next';
import { useSwrStoriesFetchAll } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesFetchAll';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  PauseIcon, PlayIcon, ShareIcon,
} from '@heroicons/react/20/solid';
import Confetti from 'react-confetti-boom';
import { useAuthClient } from '@root/src/hooks';
import { CvDetailHub } from '@components/templates/cv/CvDetailHub';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { getI18nLinkHref } from '@services/i18nPaths';
import { trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useSwrStoriesDeleteDislike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesDeleteDislike';
import { useSwrStoriesDislike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesDislike';
import { FlagIcon as FlagSolid } from '@heroicons/react/24/solid';
import { FlagIcon as FlagOutline } from '@heroicons/react/24/outline';
import { StoriesActionIcons } from '@components/templates/stories/StoriesActionIcons';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { useResumeAccessCheck } from '@components/templates/stories/hooks/useResumeAccesCheck';
import { StoriesAnswerPromptBar } from './StoriesAnswerPromptBar';
import { useSwrResumeInterviewAnswered } from '@hooks/useSwrApi/endpoints/interview/useSwrResumeInterviewAnswered';
import { useSwrInterviewGenerateQuestion } from '@hooks/useSwrApi/endpoints/ai-interview/useSwrInterviewGenerateQuestion';
import { filterRealInterviewQuestions } from '@services/interview/resumeInterviewApi';


interface SwiperStoriesCardsProps {
  initialData?: ApiSchema<'ResumeStoriesResult'>;
  locale: Locale;
  ps?: string;
  storyRoute?: string;
  syncUrl?: boolean;
}

type StoryItem = ApiSchema<'ResumeStoryCardData'>;

// A slide is either a real video card, or (for a candidate with unanswered resume-interview
// questions) one of their own blank onboarding cards — same swiper, same one-by-one navigation.
// `index` is the question's stable position in the full 3-question set (not its position in the
// filtered/sliced unanswered subset, which shifts as questions get answered).
type StoriesSlide =
  | { kind: 'onboarding'; id: string; question: string; index: number }
  | { kind: 'video'; id: string; item: StoryItem };

export default function SwiperStoriesCards({
  initialData, locale, ps, storyRoute = '/stories', syncUrl = true,
}: SwiperStoriesCardsProps) {
  const { t } = useTranslation('common');
  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isOpenShareModal, toggleShareModal] = useState(false);
  const { isAuth } = useAuthClient();
  const { withLocale } = useExtendedRouter();

  // The candidate's own resume-interview questions they haven't answered yet — shown first as
  // blank onboarding cards, prepended to the real video feed in the same swiper.
  const {
    answeredCount, isLoading: isLoadingAnswered, mutate: mutateAnswered,
  } = useSwrResumeInterviewAnswered({
    locale,
    shouldFetch: isAuth,
  });
  const {
    data: interviewQuestionsData, isLoading: isLoadingOwnQuestions,
  } = useSwrInterviewGenerateQuestion({ locale, shouldFetch: isAuth });
  // Both checks must settle before the feed renders — otherwise the video list shows first and
  // the onboarding cards pop in on top of it a moment later once the check resolves.
  const isCheckingOwnQuestions = isAuth && (isLoadingAnswered || isLoadingOwnQuestions);
  const ownQuestions = useMemo(
    () => filterRealInterviewQuestions(interviewQuestionsData?.questions ?? []).map((q) => q.question),
    [interviewQuestionsData],
  );
  // Locally track which of the 3 own questions were answered THIS session, by their stable
  // index — as soon as one's submitted, drop it from the pool immediately. Waiting on the
  // backend's answeredCount to refetch would leave the just-answered question showing until the
  // refetch resolves (and even then, count alone can't tell us WHICH one to drop).
  const [locallyAnsweredIndexes, setLocallyAnsweredIndexes] = useState<Set<number>>(new Set());
  // How many cards to show is still driven by the backend count (source of truth across
  // sessions/reloads) — which specific question fills each slot doesn't matter, except that one
  // just answered locally must never be the one shown again.
  const unansweredOwnQuestions = useMemo(() => {
    if (!isAuth) return [];
    const remaining = Math.max(0, ownQuestions.length - answeredCount);

    return ownQuestions
      .map((question, index) => ({ question, index }))
      .filter(({ index }) => !locallyAnsweredIndexes.has(index))
      .slice(0, remaining);
  }, [isAuth, ownQuestions, answeredCount, locallyAnsweredIndexes]);

  const [selectedCvSlug, setSelectedCvSlug] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [hasInteracted, setHasinteracted] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const iconTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressRefs = useRef<HTMLDivElement[]>([]);


  const {
    data, size, setSize, isValidating,
  } = useSwrStoriesFetchAll({
    locale,
    prioritisedSlug: ps,
    serverSideData: initialData,
  });


  const totalPages = initialData?.pages ?? Infinity;
  const items = useMemo(
    () => data?.flatMap((page) => page.items || []) || [],
    [data],
  );
  const hasTrackedInitialView = useRef(false);

  // Unified slide list: onboarding cards first, then the real video feed. One continuous
  // vertical swiper — onboarding cards behave exactly like real cards for navigation purposes.
  const slides: StoriesSlide[] = useMemo(() => [
    ...unansweredOwnQuestions.map(({ question, index }) => ({
      kind: 'onboarding' as const, id: `onboarding-${index}`, question, index,
    })),
    ...items.map((item, i) => ({ kind: 'video' as const, id: `${item.id}-${item.videos?.[0]?.id ?? i}`, item })),
  ], [unansweredOwnQuestions, items]);

  const activeSlide = slides[swiperIndex];
  const activeVideoItem = activeSlide?.kind === 'video' ? activeSlide.item : undefined;
  const activeVideoSlug = activeVideoItem?.slug;

  const { isChecking: isLoadingSecurityCheck, isAllowed: isAllowedPlayingVideo, check: checkResumeAccess } = useResumeAccessCheck(locale, activeVideoSlug);

  useEffect(() => {
    if (!activeVideoSlug) {
      // On an onboarding slide (or nothing loaded yet) — nothing to play.
      videoRefs.current.forEach((v) => v?.pause());
      return;
    }

    void checkResumeAccess(activeVideoSlug);
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === swiperIndex) v.play().catch(() => {});
      else {
        v.pause();
        v.currentTime = 0;
      }
    });
  }, [activeVideoSlug, checkResumeAccess, swiperIndex]);


  // Track initial story view when stories are first loaded
  useEffect(() => {
    if (items && items.length > 0 && !hasTrackedInitialView.current) {
      sendGoogleTagEvent('viewed_interview', 'Custom', { source: 'stories_page' });
      hasTrackedInitialView.current = true;
    }
  }, [items]);

  const { trigger: triggerStoriesDislike } = useSwrStoriesDislike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      if (activeVideoItem) activeVideoItem.dislikedByMe = true;
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
    },
  });

  const { trigger: triggerDeleteStoriesDislike } = useSwrStoriesDeleteDislike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      if (activeVideoItem) activeVideoItem.dislikedByMe = false;
    },
  });

  const toggleMuteAll = useCallback((value?: string) => {
    if (!value) return;

    if (value === 'true') {
      setMuted(true);
    } else if (value === 'false') {
      setMuted(false);
    } else if (value === 'prev') {
      setMuted((prev) => !prev);
    }
  }, []);


  const updateVideos = (activeIndex: number) => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === activeIndex) {
        if (isAllowedPlayingVideo && !isLoadingSecurityCheck) {
          void video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  };


  const handleStoriesStart = (index:number) => {
    const video = videoRefs.current[index];
    if (!video) return;
    video.play().catch(() => {});
    toggleMuteAll('false');
    setHasinteracted(true);
  };

  const pauseVideo = useCallback((index: number) => {
    const video = videoRefs.current[index];
    if (!video || video.paused) return;

    video.pause();
    setIsPlaying(false);
  }, []);

  const handleVideoClick = useCallback(
    (index: number) => {
      if (!hasInteracted) return;

      const video = videoRefs.current[index];
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
        setIsPlaying(true);
        toggleMuteAll('false');
      } else {
        video.pause();
        setIsPlaying(false);
        toggleMuteAll('true');
      }

      // Show icon for 1 second
      setShowIcon(true);
      if (iconTimeout.current) clearTimeout(iconTimeout.current);
      iconTimeout.current = setTimeout(() => setShowIcon(false), 1000);
    },
    [hasInteracted, toggleMuteAll],
  );

  useEffect(() => {
    if (!syncUrl) return;
    if (!activeVideoSlug) return;

    window.history.pushState(
      null,
      '',
      withLocale(`${storyRoute}?ps=${activeVideoSlug}`),
    );
  }, [activeVideoSlug, storyRoute, syncUrl, withLocale]);

  // Question the bottom bar should answer: the onboarding slide's own question, or the current
  // video card's linked question (as long as it's not the viewer's own card). Shown even if
  // already answered — resubmitting just upserts the existing answer, no need to hide the bar.
  const activeVideoQuestion = activeVideoItem?.videos?.[0]?.question;
  const barQuestion = activeSlide?.kind === 'onboarding'
    ? activeSlide.question
    : (isAuth && !!activeVideoQuestion && activeVideoItem?.slug !== headerInfoData?.userSlug)
      ? activeVideoQuestion
      : undefined;

  return (
    <>
      {/* Flex column pinned to the viewport (minus the fixed header) so the card never pushes
          the page taller than 100dvh — an overflow here would let the page scroll, which desyncs
          from the swiper's own card-to-card navigation. */}
      <div className="flex h-[calc(100dvh-var(--header-height,4rem))] flex-col">
        <div
          // Bottom margin just big enough to clear the fixed prompt bar: pill (~52px) + mt-2 gap
          // (8px) + mission-chips row (~26px) + the bar's own bottom offset (bottom-3/lg:bottom-8).
          className="relative mb-[calc(8rem+env(safe-area-inset-bottom))] min-h-0 flex-1 max-md:px-3 md:container md:w-125 lg:mb-[calc(7rem+env(safe-area-inset-bottom))] xl:w-200"
        >
      <ShareModal
        isOpen={isOpenShareModal}
        onClose={() => toggleShareModal(false)}
        ogTitle={t('meta.stories.share.modal.title')}
        ogDescription={t('meta.stories.share.modal.title')}
        pathnameShareUrl={`${getI18nLinkHref(`/stories?ps=${activeVideoSlug}`, locale)}`}
      />

      {selectedCvSlug && (
        <Modal
          isOpen
          onClose={() => {
            setSelectedCvSlug(null);
          }}
          size="full"
          className="transform-none!"
          body={(
            <>
              <CvDetailHub
                trackingData={{
                  listType: 'stories',
                }}
                slug={selectedCvSlug}
                isAuthenticated={isAuth}
                closeModal={() => {
                  setSelectedCvSlug(null);
                }}
              />
            </>
          )}
        />
      )}

      {isCheckingOwnQuestions ? (
        <div className="flex size-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
      <>
      <Swiper
        direction="vertical"
        modules={[Navigation, Mousewheel, Keyboard]}
        keyboard={{ enabled: true, onlyInViewport: false }}
        // Trackpads fire a burst of wheel events per swipe gesture; without a time/delta
        // threshold that burst can cross the "advance one slide" threshold twice, skipping
        // a card. thresholdTime throttles how often a single gesture can trigger a change.
        mousewheel={{ thresholdDelta: 30, thresholdTime: 400, forceToAxis: true }}
        navigation={{ nextEl: '.next-btn', prevEl: '.prev-btn' }}
        slidesPerView={1}
        observeParents
        observer
        className="h-full"
        onSlideChange={(swiper) => {
          const index = swiper.activeIndex;
          setHasinteracted(true);
          setSwiperIndex(index);
          const slide = slides[index];
          if (!slide) return;
          updateVideos(index);

          if (slide.kind === 'video') {
            trackEvent({
              eventType: 'view',
              listType: 'stories',
              entityType: 'resume',
              entityId: slide.item.slug ?? '',
              viewContext: 'detail',
              ...(userId && { userId: userId.toString() }),
              metadata: [
                { key: 'video', value: slide.item.slug ?? '' },
              ],
            });
            sendGoogleTagEvent('viewed_interview', 'Custom', { source: 'stories_page' });

            // Fetch next page if near end of the real video feed (index offset by the
            // onboarding cards prepended ahead of it).
            const realIndex = index - unansweredOwnQuestions.length;
            if (!isValidating && size < totalPages && realIndex >= items.length - 2) {
              void setSize(size + 1);
            }
          }
        }}
      >
        {slides.map((slide, index) => {
          if (slide.kind === 'onboarding') {
            return (
              <SwiperSlide key={slide.id}>
                <div className="flex size-full flex-col">
                  <div className="min-h-0 flex-1" />
                  <div className="shrink-0 px-3 pb-10 pt-5 text-center text-lg font-medium text-ink md:pb-16 md:text-xl">
                    {slide.question}
                  </div>
                </div>
              </SwiperSlide>
            );
          }

          const { item } = slide;
          const firstVideo = item.videos?.[0];
          if (!firstVideo) return null;

          return (
            <SwiperSlide key={slide.id}>
              <div className="flex size-full flex-col">
              <div className="relative min-h-0 flex-1 overflow-hidden rounded-3xl! border-y-8 border-surface-100">

                {(isLoadingSecurityCheck || !isAllowedPlayingVideo)
                  && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black opacity-100">
                      <div className="flex items-center space-x-3">
                        <div className="size-4 animate-bounce rounded-full bg-secondary" />
                        <div className="size-4 animate-bounce rounded-full bg-white [animation-delay:0.15s]" />
                        <div className="size-4 animate-bounce rounded-full bg-secondary [animation-delay:0.3s]" />
                      </div>

                      {/* We need server side check here */}
                      {!isAuth
                        && (
                        <LinkStyled
                          href="/login"
                          styling="solid"
                          size="sm"
                          color="secondary"
                          rounding="full"
                          className="mt-3"
                          aria-label={t('login.meta.title')}
                        >
                          {t('login.meta.title')}
                        </LinkStyled>
                        )}
                    </div>
                  )}

                {/* Interaction buttons */}
                <div className="absolute right-4 top-8 z-2 flex flex-col items-center gap-5">

                  {/* Mute / Unmute */}
                  <StoriesActionIcons
                    onClick={() => toggleMuteAll('prev')}
                    icon={muted ? <SpeakerXMarkIcon className="size-5" /> : <SpeakerWaveIcon className="size-5" />}
                  />

                  {/* Share */}
                  <StoriesActionIcons
                    onClick={() => toggleShareModal(true)}
                    icon={<ShareIcon className="size-5" />}
                  />

                  <StoriesActionIcons
                    onClick={!isAuth ? undefined : () => {
                      void (item.dislikedByMe ? triggerDeleteStoriesDislike() : triggerStoriesDislike());
                    }}
                    isLink={!isAuth}
                    href={!isAuth ? '/login' : undefined}
                    icon={item.dislikedByMe ? <FlagSolid className="size-5 text-ink" />
                      : <FlagOutline className="size-5 text-ink" />}
                  />

                </div>


                <div
                  role="presentation"
                  className="absolute left-0 top-0 z-1 h-1 w-full cursor-pointer bg-gray-400"
                  onClick={(e) => {
                    const activeVideo = videoRefs.current[swiperIndex];
                    if (!activeVideo) return;

                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    activeVideo.currentTime = (clickX / rect.width) * activeVideo.duration;
                  }}
                >
                  <div
                    role="presentation"
                    className="relative h-1 w-full cursor-pointer bg-gray-400"
                    onClick={(e) => {
                      const video = videoRefs.current[swiperIndex];
                      if (!video) return;

                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      video.currentTime = (clickX / rect.width) * video.duration;
                    }}
                  >
                    <div
                      ref={(el) => {
                        if (el) progressRefs.current[index] = el;
                      }}
                      className="h-1 bg-secondary transition-all duration-200 ease-linear"
                    />
                  </div>
                </div>
                <video
                  ref={(el) => {
                    if (el) videoRefs.current[index] = el;
                  }}
                  src={Math.abs(index - swiperIndex) <= 1
                    ? firstVideo.url
                    : undefined}
                  className="size-full bg-[#000000] object-cover md:object-contain"
                  loop
                  onClick={() => handleVideoClick(index)}
                  muted={muted}
                  onTimeUpdate={(e) => {
                    const video = e.currentTarget;
                    const progressEl = progressRefs.current[index];
                    if (!progressEl) return;
                    progressEl.style.width = `${(video.currentTime / video.duration) * 100}%`;
                  }}
                  playsInline
                />

                {!hasInteracted
                  && (
                    <div
                      role="presentation"
                      onClick={() => handleStoriesStart(index)}
                      className="absolute inset-0 z-1 flex flex-col items-center justify-center bg-dark/30"
                    >
                      <div
                        className="flex size-16 items-center justify-center rounded-full bg-black/60"
                      >
                        <PlayIcon className="size-10 text-white opacity-90" />
                      </div>
                    </div>

                  )}

                {/* Overlay Icon */}
                {showIcon && (
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60"
                  >
                    {isPlaying ? (
                      <PlayIcon className="size-10 animate-fade text-white opacity-90" />
                    ) : (
                      <PauseIcon className="size-10 animate-fade text-white opacity-90" />
                    )}
                  </div>
                )}

                {showConfetti && (
                  <Confetti />
                )}

                <div
                  className="absolute top-1 flex h-25 w-full items-center justify-start bg-story-top  p-3 text-white"
                >

                  <button
                    type="button"
                    onClick={() => {
                      pauseVideo(index);
                      setSelectedCvSlug(item.slug ?? '');
                    }}
                    className="flex items-center justify-start gap-2 text-left transition hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                    aria-label={t('stories.view.profile', 'View profile')}
                  >
                    {item.imgUrl && (
                      <div className="min-w-14">
                        <Avatar
                          size="sm"
                          imgPath={item.imgUrl}
                          aspectRatio="square-uncertain"
                          extractImageName
                          alt={item.slug}
                          className="mx-auto"
                        />
                      </div>
                    )}
                    {item.leadExperience && (
                      <div className="pr-10 md:pr-0">
                        <div className="max-w-81.25 overflow-hidden truncate whitespace-nowrap text-sm font-bold text-white md:text-lg">
                          {item.leadExperience?.title}
                        </div>
                        <div className="mb-2 text-xs text-white">
                          @
                          {item.leadExperience?.company}
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* Question moved below the card (not overlaid on the video) so it reads as
                  "here's what to answer", not a caption on someone else's video. */}
              <div className="shrink-0 px-3 pb-10 pt-5 text-center text-lg font-medium text-ink md:pb-16 md:text-xl">
                {firstVideo.question}
              </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {/* Custom navigation buttons */}
      <div className="absolute -right-16 top-1/2 z-2 -mt-12 hidden flex-col gap-4 md:flex">
        <button type="button" className="prev-btn rounded-full bg-surface p-2 text-ink">
          <ArrowUpIcon className="size-5" />
        </button>
        <button type="button" className="next-btn rounded-full bg-surface p-2 text-ink">
          <ArrowDownIcon className="size-5" />
        </button>
      </div>
      </>
      )}

      </div>
      </div>

      {/* Bottom prompt bar — always visible, always scoped to the current slide's question
          (onboarding card's own question, or the current video card's linked question). */}
      <StoriesAnswerPromptBar
        question={barQuestion}
        locale={locale}
        isAuthenticated={isAuth}
        firstMessage={t('audio-interview.bestie.message')}
        onAnswered={() => {
          if (activeSlide?.kind === 'onboarding') {
            const answeredIndex = activeSlide.index;
            setLocallyAnsweredIndexes((prev) => new Set(prev).add(answeredIndex));
          }
          void mutateAnswered();
        }}
      />
    </>
  );
}
